import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNls } from '../../nls/nlsProvider'
import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import type { HelperReadinessModel } from './helperReadinessModel'
import {
  HELPER_BLOCKER_REASON_LABELS,
  type HelperOpenBlocker,
} from '../../types/helperBlocker'
import {
  listPendingMutations,
  type HelperMutationRecord,
} from '../../services/helper/helperOutboxDb'
import { drainOutbox } from '../../services/helper/syncWorker'
import { Button, KeyValueRow, LedgerRow } from '../Common'

export type HelperFieldTodayProps = {
  items: HelperAssignedItem[]
  readinessByItemId?: Map<string, HelperReadinessModel>
  openBlockers?: HelperOpenBlocker[]
  onOpenItem?: (itemId: string) => void
  workspaceId?: string
  initialMutations?: HelperMutationRecord[]
}

const getMutationLabel = (record: HelperMutationRecord): string => {
  switch (record.type) {
    case 'CHECKLIST_TOGGLE':
      return 'Checklist item update'
    case 'CHECKLIST_PROOF_UPLOAD':
      return 'Proof upload'
    case 'CHECKLIST_COMMENT':
      return 'Comment'
    case 'CHECKLIST_VOICE_MEMO':
      return 'Voice memo'
    case 'STOCK_COUNT_ROUND':
      return 'Stock count round'
    case 'STOCK_SCAN_SUGGESTION':
      return 'Scan suggestion'
    case 'PENDING_EXPENSE_INTAKE':
      return 'Expense submission'
    case 'WORKSPACE_ACTIVITY':
      return 'Activity log'
    default:
      return record.type
  }
}

export const HelperFieldToday = ({
  items,
  readinessByItemId,
  openBlockers = [],
  onOpenItem,
  initialMutations,
}: HelperFieldTodayProps) => {
  const { t } = useNls()
  const [mutations, setMutations] = useState<HelperMutationRecord[]>(initialMutations ?? [])
  const [retrying, setRetrying] = useState(false)

  const refreshMutations = useCallback(async () => {
    try {
      const pending = await listPendingMutations()
      setMutations(pending)
    } catch {
      setMutations([])
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch of mutations on mount
    void refreshMutations()
    if (process.env.NODE_ENV === 'test') return
    const timer = setInterval(() => void refreshMutations(), 5000)
    return () => clearInterval(timer)
  }, [refreshMutations])

  const handleRetry = useCallback(async () => {
    setRetrying(true)
    try {
      await drainOutbox()
    } finally {
      await refreshMutations()
      setRetrying(false)
    }
  }, [refreshMutations])

  const startOfTodayMs = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  }, [])

  const blockersRaisedToday = useMemo(
    () => openBlockers.filter((b) => b.createdAtMs >= startOfTodayMs),
    [openBlockers, startOfTodayMs],
  )

  const blockedCount = useMemo(
    () =>
      items.filter((item) => {
        const readiness = readinessByItemId?.get(item.id)
        return (
          readiness?.status === 'blocked' ||
          (readiness?.openBlockers && readiness.openBlockers.length > 0)
        )
      }).length,
    [items, readinessByItemId],
  )

  const readyCount = useMemo(
    () =>
      items.filter((item) => {
        const readiness = readinessByItemId?.get(item.id)
        return readiness?.status === 'clear'
      }).length,
    [items, readinessByItemId],
  )

  const todayDateString = useMemo(() => {
    const now = new Date()
    return now.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
  }, [])

  return (
    <div className="flex flex-col pb-6 text-[var(--text-strong)]" data-testid="helper-field-today">
      {/* Schedule section */}
      <div className="flex items-center justify-between px-3.5 pt-3 pb-1.5 text-[11px] font-semibold text-[var(--muted)] uppercase tracking-wider border-b border-[var(--border-subtle)]">
        <span>
          {todayDateString} · {items.length} {items.length === 1 ? 'task' : 'tasks'}
          {blockedCount > 0 ? ` · ${blockedCount} blocked` : ''}
        </span>
        <span className="text-[var(--accent-strong)] font-bold">{readyCount} ready</span>
      </div>

      <div className="flex flex-col" data-testid="helper-field-today-schedule">
        {items.length > 0 ? (
          items.map((item) => {
            const readiness = readinessByItemId?.get(item.id)
            const time =
              item.scheduleTimeFrom ?? (readiness?.dueLabel || '—')
            const locationPart = (item as { locationName?: string }).locationName
              ? `${(item as { locationName?: string }).locationName} · `
              : ''
            const readinessPart =
              readiness?.requirementLabels?.join(', ') ||
              readiness?.dependencyReasons?.join(', ') ||
              (readiness?.status === 'blocked' ? 'Blocked' : 'Ready')
            const subtitle = `${locationPart}${readinessPart}`

            return (
              <LedgerRow
                key={item.id}
                leading={<span className="font-mono text-[11px]">{time}</span>}
                title={item.title}
                subtitle={subtitle}
                trailingChevron
                onClick={() => onOpenItem?.(item.id)}
                testId={`helper-field-schedule-row-${item.id}`}
              />
            )
          })
        ) : (
          <div className="px-3.5 py-4 text-xs text-[var(--muted)]">
            {t('NLSHelperFieldNoSchedule')}
          </div>
        )}
      </div>

      {/* Sync queue section */}
      <div className="flex items-center justify-between px-3.5 pt-5 pb-1.5 text-[11px] font-semibold text-[var(--muted)] uppercase tracking-wider border-b border-[var(--border-subtle)]">
        <span>{t('NLSHelperFieldSyncQueueTitle')}</span>
        <div className="flex items-center gap-2">
          <span className="font-mono font-bold text-[var(--text-strong)]">{mutations.length}</span>
          {mutations.length > 0 ? (
            <Button
              size="sm"
              variant="tertiary"
              onClick={() => void handleRetry()}
              disabled={retrying}
              className="!py-0.5 !px-2 !text-[11px]"
              data-testid="helper-field-sync-retry"
            >
              {t('NLSHelperFieldSyncRetry')}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col" data-testid="helper-field-today-sync-queue">
        {mutations.length > 0 ? (
          mutations.map((mutation) => (
            <KeyValueRow
              key={mutation.id}
              label={getMutationLabel(mutation)}
              value={mutation.status.toLowerCase()}
              tone={
                mutation.status === 'FAILED'
                  ? 'bad'
                  : mutation.status === 'SYNCING'
                  ? 'ok'
                  : 'warn'
              }
              testId={`helper-field-mutation-${mutation.id}`}
            />
          ))
        ) : (
          <div className="px-3.5 py-3 text-xs text-[var(--muted)]">
            {t('NLSHelperFieldSyncQueueEmpty')}
          </div>
        )}
      </div>

      {/* Blockers raised today section */}
      <div className="flex items-center justify-between px-3.5 pt-5 pb-1.5 text-[11px] font-semibold text-[var(--muted)] uppercase tracking-wider border-b border-[var(--border-subtle)]">
        <span>{t('NLSHelperFieldBlockersTitle')}</span>
        <span className="font-mono font-bold text-[var(--text-strong)]">
          {blockersRaisedToday.length} open
        </span>
      </div>

      <div className="flex flex-col" data-testid="helper-field-today-blockers">
        {blockersRaisedToday.length > 0 ? (
          blockersRaisedToday.map((blocker) => (
            <KeyValueRow
              key={blocker.id}
              label={`${blocker.itemTitle}: ${
                HELPER_BLOCKER_REASON_LABELS[blocker.reason] ?? blocker.reason
              }`}
              value="open"
              tone="warn"
              sublabel={blocker.note || undefined}
              testId={`helper-field-blocker-${blocker.id}`}
            />
          ))
        ) : (
          <div className="px-3.5 py-3 text-xs text-[var(--muted)]">
            {t('NLSHelperFieldNoBlockers')}
          </div>
        )}
      </div>
    </div>
  )
}
